import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { Plus, Zap, ZapOff, Trash2, Play, Pause, MoreHorizontal, Search } from "lucide-react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import {
  useWorkflows,
  useDeleteWorkflow,
  useActivateWorkflow,
  useDeactivateWorkflow,
} from "@/hooks/use-workflows"
import type { Workflow } from "@/lib/types"

const TRIGGER_EVENT_LABELS: Record<string, string> = {
  DealCreated: "Nowy deal",
  DealPhaseChanged: "Zmiana fazy deala",
  DealWon: "Deal wygrany",
  DealLost: "Deal przegrany",
  TaskCreated: "Nowe zadanie",
  TaskCompleted: "Zadanie ukończone",
  CompanyCreated: "Nowa firma",
  ContactCreated: "Nowy kontakt",
  ServiceTicketCreated: "Nowe zgłoszenie",
  ServiceTicketPhaseChanged: "Zmiana fazy zgłoszenia",
  ServiceTicketResolved: "Zgłoszenie rozwiązane",
  InvoiceIssued: "Faktura wystawiona",
  InvoicePaid: "Faktura opłacona",
  QuoteAccepted: "Oferta zaakceptowana",
  QuoteRejected: "Oferta odrzucona",
}

export function WorkflowsListPage() {
  const navigate = useNavigate()
  const [search, setSearch] = useState("")
  const [statusFilter, setStatusFilter] = useState<string>("all")

  const filters =
    statusFilter === "active"
      ? { is_active: true, search: search || undefined }
      : statusFilter === "inactive"
        ? { is_active: false, search: search || undefined }
        : { search: search || undefined }

  const { data, isLoading } = useWorkflows(filters)
  const deleteMutation = useDeleteWorkflow()
  const activateMutation = useActivateWorkflow()
  const deactivateMutation = useDeactivateWorkflow()

  const workflows = data?.data ?? []

  const handleDelete = (wf: Workflow) => {
    if (!confirm(`Usunąć workflow "${wf.name}"?`)) return
    deleteMutation.mutate(wf.id, {
      onSuccess: () => toast.success("Workflow usunięty"),
    })
  }

  const handleToggleActive = (wf: Workflow) => {
    if (wf.is_active) {
      deactivateMutation.mutate(wf.id, {
        onSuccess: () => toast.success("Workflow dezaktywowany"),
      })
    } else {
      activateMutation.mutate(wf.id, {
        onSuccess: () => toast.success("Workflow aktywowany"),
        onError: (e) => toast.error(e instanceof Error ? e.message : "Błąd aktywacji"),
      })
    }
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="page-title">
            Automatyzacje
          </h1>
          <p className="text-sm mt-1 text-muted-foreground">
            Zarządzaj automatycznymi akcjami wyzwalanymi zdarzeniami
          </p>
        </div>
        <Button onClick={() => navigate("/automations/new")}>
          <Plus size={16} />
          Nowy workflow
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="text"
            placeholder="Szukaj workflow..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-border bg-card text-sm text-foreground"
          />
        </div>
        <div className="flex gap-1 rounded-lg p-1 bg-muted">
          {[
            { key: "all", label: "Wszystkie" },
            { key: "active", label: "Aktywne" },
            { key: "inactive", label: "Nieaktywne" },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setStatusFilter(f.key)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                statusFilter === f.key
                  ? "bg-card text-foreground"
                  : "text-muted-foreground"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-20 rounded-lg animate-pulse bg-muted"
            />
          ))}
        </div>
      ) : workflows.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 rounded-lg border border-dashed">
          <Zap size={48} className="text-muted-foreground" />
          <p className="mt-4 text-lg font-medium text-foreground">
            Brak workflow
          </p>
          <p className="text-sm mt-1 text-muted-foreground">
            Utwórz pierwszy workflow, by zautomatyzować procesy
          </p>
          <Button onClick={() => navigate("/automations/new")} className="mt-4">
            <Plus size={16} />
            Nowy workflow
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {workflows.map((wf) => (
            <WorkflowCard
              key={wf.id}
              workflow={wf}
              onEdit={() => navigate(`/automations/${wf.id}`)}
              onToggle={() => handleToggleActive(wf)}
              onDelete={() => handleDelete(wf)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function WorkflowCard({
  workflow,
  onEdit,
  onToggle,
  onDelete,
}: {
  workflow: Workflow
  onEdit: () => void
  onToggle: () => void
  onDelete: () => void
}) {
  const [showMenu, setShowMenu] = useState(false)

  return (
    <div
      className="rounded-lg border p-4 transition-colors cursor-pointer hover:shadow-sm bg-card"
      onClick={onEdit}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className={`flex h-10 w-10 items-center justify-center rounded-lg shrink-0 ${
              workflow.is_active ? "bg-green-500/10" : "bg-muted"
            }`}
          >
            {workflow.is_active ? (
              <Zap size={20} className="text-green-500" />
            ) : (
              <ZapOff size={20} className="text-muted-foreground" />
            )}
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-medium truncate text-foreground">
              {workflow.name}
            </h3>
            <div className="flex items-center gap-2 mt-0.5">
              <span
                className={`text-xs px-2 py-0.5 rounded-full ${
                  workflow.is_active
                    ? "bg-green-500/10 text-green-500"
                    : "bg-muted text-muted-foreground"
                }`}
              >
                {workflow.is_active ? "Aktywny" : "Nieaktywny"}
              </span>
              {workflow.trigger_event && (
                <span className="text-xs text-muted-foreground">
                  {TRIGGER_EVENT_LABELS[workflow.trigger_event] ?? workflow.trigger_event}
                </span>
              )}
              <span className="text-xs text-muted-foreground">
                {workflow.steps_count} {workflow.steps_count === 1 ? "krok" : "kroków"}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={onToggle}
            className="p-2 rounded-md transition-colors text-muted-foreground hover:bg-muted"
            title={workflow.is_active ? "Dezaktywuj" : "Aktywuj"}
          >
            {workflow.is_active ? <Pause size={16} /> : <Play size={16} />}
          </button>
          <div className="relative">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="p-2 rounded-md transition-colors text-muted-foreground hover:bg-muted"
            >
              <MoreHorizontal size={16} />
            </button>
            {showMenu && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowMenu(false)} />
                <div className="absolute right-0 top-full mt-1 z-50 w-40 rounded-lg border shadow-lg py-1 bg-card">
                  <button
                    onClick={() => {
                      setShowMenu(false)
                      onEdit()
                    }}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-muted text-foreground"
                  >
                    Edytuj
                  </button>
                  <button
                    onClick={() => {
                      setShowMenu(false)
                      onDelete()
                    }}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-muted text-red-500"
                  >
                    <span className="flex items-center gap-2">
                      <Trash2 size={14} />
                      Usuń
                    </span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
